import { resolveImageSrc } from './image'
import {
  SITE_NAME,
  SITE_SHARE_IMAGE,
  SITE_URL,
  type SeoData,
  absoluteUrl,
  articleLd,
  breadcrumbLd,
  latestDate,
  publisherLd,
  stripHtmlToText,
  truncate,
} from './seo'

// Site-wide primitives live in utils/seo.ts; re-exported here so the existing
// guide-tree imports keep working from a single place.
export { SITE_NAME, SITE_SHARE_IMAGE, SITE_URL, absoluteUrl, stripHtmlToText, truncate }

// ─── Guide game registry ──────────────────────────────────────────────────────
// Single source of truth for guide-enabled games. Keyed by the URL segment
// (/game/guide/<key>). Replaces the divergent maps that used to live in the
// hub page (GUIDE_GAMES) and echo page (GUIDE_SLUGS).
export type GuideGame = {
  /** Backend guide gameSlug (path param on /guides/:gameSlug/...) */
  slug: string
  /** games collection ObjectId */
  gameId: string
  title: string
  /** Search abbreviation with heavy query volume (WuWa / ZZZ) */
  abbrev: string
  copyright: string
  /** Thai UI labels for the two gear categories */
  weaponLabel: string
  echoLabel: string
  /** English terms for the two gear categories */
  weaponTermEn: string
  echoTermEn: string
  /** URL segment for gear detail pages (weapon|wengine / echo|drivedisc) */
  weaponPath: string
  echoPath: string
}

export const GUIDE_GAMES: Record<string, GuideGame> = {
  'wuthering-waves': {
    slug: 'wutheringwaves',
    gameId: '663ba9d6a055571913a15f57',
    title: 'Wuthering Waves',
    abbrev: 'WuWa',
    copyright: 'Kuro Games',
    weaponLabel: 'อาวุธ',
    echoLabel: 'เอคโค่',
    weaponTermEn: 'Weapons',
    echoTermEn: 'Echoes',
    weaponPath: 'weapon',
    echoPath: 'echo',
  },
  zzz: {
    slug: 'zzz',
    gameId: '6315694d6b557e56fc6b3b60',
    title: 'Zenless Zone Zero',
    abbrev: 'ZZZ',
    copyright: 'miHoYo',
    weaponLabel: 'W-Engine',
    echoLabel: 'Drive Disc',
    weaponTermEn: 'W-Engines',
    echoTermEn: 'Drive Discs',
    weaponPath: 'wengine',
    echoPath: 'drivedisc',
  },
}

/**
 * Resolve any /game/guide/[id] param to the canonical URL key.
 * Accepts the URL key itself, the backend gameSlug, or the games-collection
 * ObjectId (legacy direct-id URLs) so those can be 301'd to the slug URL.
 */
export const resolveGuideKey = (param: unknown): string | null => {
  if (typeof param !== 'string' || !param) return null
  if (GUIDE_GAMES[param]) return param
  const byAltId = Object.entries(GUIDE_GAMES).find(
    ([, g]) => g.gameId === param || g.slug === param,
  )
  return byAltId ? byAltId[0] : null
}

// ─── Text helpers ─────────────────────────────────────────────────────────────
const metaName = (value: any): string =>
  value && typeof value === 'object' ? value.name || '' : ''

/** Guide pages always emit keywords and JSON-LD, so both are required here. */
export type GuideSeoData = Omit<SeoData, 'keywords' | 'jsonLd'> & {
  keywords: string[]
  jsonLd: Record<string, any>[]
}

// ─── Hub page ─────────────────────────────────────────────────────────────────
export const buildGuideHubSeo = (opts: {
  gameKey: string
  gameData: any
  ogImage?: string
}): GuideSeoData => {
  const game = GUIDE_GAMES[opts.gameKey]
  const canonicalPath = `/game/guide/${opts.gameKey}`
  const synopsis = truncate(stripHtmlToText(opts.gameData?.synopsis), 120)

  // Thai-primary metadata with English entity terms — mirrors how Thai players
  // actually search ("[English entity] + [Thai modifier]"); see SEO research.
  const gearTerms =
    game.slug === 'zzz' ? 'W-Engine Drive Disc' : 'อาวุธ Echo'
  const title = `ไกด์ ${game.title} (${game.abbrev}) ตัวละคร Build ${gearTerms} จัดทีม | ${SITE_NAME}`
  const description =
    `ไกด์ ${game.title} (${game.abbrev}) ฉบับสมบูรณ์ รวมแนวทางการปั้นตัวละครทุกตัว ` +
    `${game.weaponLabel} ${game.echoLabel} การจัดทีม และบทความไกด์ล่าสุด ` +
    `Complete ${game.title} guide: character builds, ${game.weaponTermEn.toLowerCase()}, ` +
    `${game.echoTermEn.toLowerCase()}, and team comps.` +
    (synopsis ? ` ${synopsis}` : '')

  const keywords = [
    `${game.title} ไกด์`,
    `${game.abbrev} ไกด์`,
    `${game.title} guide`,
    `${game.abbrev} build`,
    `${game.title} ตัวละคร`,
    `${game.abbrev} จัดทีม`,
    `${game.title} ${game.weaponTermEn.toLowerCase()}`,
    `${game.title} ${game.echoTermEn.toLowerCase()}`,
    `${game.abbrev} tier list`,
  ]

  const ogImage = opts.ogImage ? resolveImageSrc(opts.ogImage) : SITE_SHARE_IMAGE

  const videoGameLd: Record<string, any> = {
    '@type': 'VideoGame',
    name: game.title,
    ...(opts.gameData?.releaseDate ? { datePublished: opts.gameData.releaseDate } : {}),
    ...(opts.gameData?.publishers?.length
      ? { publisher: opts.gameData.publishers.map((p: any) => ({ '@type': 'Organization', name: p.title })) }
      : {}),
  }

  return {
    title,
    description: truncate(description, 300),
    keywords,
    canonicalPath,
    ogType: 'website',
    ogImage,
    ogImageAlt: `${game.title} guide`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description: truncate(description, 300),
        url: absoluteUrl(canonicalPath),
        inLanguage: 'th',
        about: videoGameLd,
        publisher: publisherLd,
      },
      breadcrumbLd([
        { name: 'หน้าแรก', path: '/' },
        { name: `ไกด์ ${game.title}` },
      ]),
    ],
  }
}

// ─── Character build page ─────────────────────────────────────────────────────
export const buildCharacterBuildSeo = (opts: {
  gameKey: string
  characterId: string
  character: any | null
  builds?: any[]
}): GuideSeoData => {
  const game = GUIDE_GAMES[opts.gameKey] || GUIDE_GAMES['wuthering-waves']
  const canonicalPath = `/game/guide/${opts.gameKey}/char-build/${opts.characterId}`
  const c = opts.character
  const name = c?.name || ''
  const isZzz = game.slug === 'zzz'

  const element = metaName(c?.element)
  const weaponType = metaName(c?.weapon)
  const star = metaName(c?.star)
  const traits = [element, weaponType, star].filter(Boolean).join(' · ')
  const excerpt = truncate(stripHtmlToText(c?.excerpt || c?.description), 110)

  // "[Name] Build" is the dominant query in both languages; แนวทางการปั้น is the
  // Thai phrase that ranks (จัดบิ้ว is speech-only slang — verified zero volume).
  const title = isZzz
    ? `${name} Build แนวทางการปั้น W-Engine ดิสก์ไดรฟ์ จัดทีม | Zenless Zone Zero (ZZZ)`
    : `${name} Build แนวทางการปั้น อาวุธ เซ็ต Echo จัดทีม | Wuthering Waves (WuWa)`

  const description =
    (isZzz
      ? `แนวทางการปั้น ${name} ใน Zenless Zone Zero (ZZZ) W-Engine ที่ดีที่สุด เซ็ต Drive Disc ค่าสถานะที่ควรเน้น พร้อมลำดับการอัปสกิล คอมโบ และวิธีจัดทีม`
      : `แนวทางการปั้น ${name} ใน Wuthering Waves (WuWa) อาวุธที่ดีที่สุด เซ็ต Echo ค่าสถานะที่ควรเน้น พร้อมลำดับการอัปสกิล คอมโบ และวิธีจัดทีม`) +
    (traits ? ` (${traits})` : '') +
    (excerpt ? ` ${excerpt}` : '')

  const keywords = isZzz
    ? [
        `${name} build`,
        `zzz ${name} build`,
        `${name} zzz`,
        `${name} ไกด์`,
        `${name} จัดทีม`,
        `${name} w engine`,
        `${name} drive disc`,
        `${name} ดิสก์ไดรฟ์`,
        `zzz แนวทางการปั้น ${name}`,
      ]
    : [
        `${name} build`,
        `${name} wuwa`,
        `${name} wuthering waves`,
        `ไกด์ ${name}`,
        `${name} จัดทีม`,
        `${name} best weapon`,
        `${name} echo set`,
        `${name} อาวุธ`,
        `wuthering waves แนวทางการปั้น ${name}`,
      ]

  const ogImage = c?.imageUrl ? resolveImageSrc(c.imageUrl) : SITE_SHARE_IMAGE
  const buildDates = (opts.builds || []).map((b: any) => b?.updatedAt).filter(Boolean)

  return {
    title,
    description: truncate(description, 300),
    keywords,
    canonicalPath,
    ogType: 'article',
    ogImage,
    ogImageAlt: name ? `${name} — ${game.title}` : game.title,
    noindex: !name,
    jsonLd: [
      articleLd({
        headline: `${name} Build แนวทางการปั้น | ${game.title} (${game.abbrev})`,
        description: truncate(description, 300),
        canonicalPath,
        image: c?.imageUrl ? resolveImageSrc(c.imageUrl) : undefined,
        datePublished: c?.createdAt,
        dateModified: latestDate(c?.updatedAt, ...buildDates),
        about: {
          '@type': 'VideoGameCharacter',
          name,
          ...(element
            ? { additionalProperty: { '@type': 'PropertyValue', name: 'element', value: element } }
            : {}),
        },
      }),
      breadcrumbLd([
        { name: 'หน้าแรก', path: '/' },
        { name: `ไกด์ ${game.title}`, path: `/game/guide/${opts.gameKey}` },
        { name: `${name} Build` },
      ]),
    ],
  }
}

// ─── Loadout item (weapon / W-Engine / echo / drive disc) pages ───────────────
export const buildLoadoutItemSeo = (opts: {
  gameKey: string
  itemId: string
  item: any | null
  kind: 'weapon' | 'echo'
}): GuideSeoData => {
  const game = GUIDE_GAMES[opts.gameKey] || GUIDE_GAMES['wuthering-waves']
  const isZzz = game.slug === 'zzz'
  const pathSegment = opts.kind === 'weapon' ? game.weaponPath : game.echoPath
  const canonicalPath = `/game/guide/${opts.gameKey}/${pathSegment}/${opts.itemId}`
  const item = opts.item
  const name = item?.name || ''
  const detail = truncate(stripHtmlToText(item?.description || item?.story), 120)

  let title: string
  let description: string
  let keywords: string[]

  if (opts.kind === 'weapon') {
    title = isZzz
      ? `W-Engine ${name} สเตตัส เอฟเฟกต์ เหมาะกับใคร | Zenless Zone Zero (ZZZ)`
      : `อาวุธ ${name} สเตตัส สกิล เหมาะกับใคร | Wuthering Waves (WuWa)`
    description = isZzz
      ? `ข้อมูล W-Engine ${name} ใน Zenless Zone Zero (ZZZ) ค่าสถานะ เอฟเฟกต์ เหมาะกับตัวละครไหน`
      : `ข้อมูลอาวุธ ${name} ใน Wuthering Waves (WuWa) ค่าสถานะ สกิลอาวุธ เหมาะกับตัวละครไหน`
    keywords = isZzz
      ? [`${name} zzz`, `${name} w engine`, `zzz w-engine ${name}`, `w-engine ${name} เหมาะกับใคร`, `zzz อาวุธ ${name}`]
      : [`${name} wuwa`, `${name} wuthering waves`, `wuthering waves อาวุธ ${name}`, `อาวุธ ${name} เหมาะกับใคร`, `${name} weapon`]
  } else {
    title = isZzz
      ? `Drive Disc ${name} (ดิสก์ไดรฟ์) โบนัสเซ็ต เหมาะกับตัวไหน | Zenless Zone Zero (ZZZ)`
      : `Echo ${name} (เอคโค่) สกิล เซ็ตโบนัส เหมาะกับตัวไหน | Wuthering Waves (WuWa)`
    description = isZzz
      ? `ข้อมูล Drive Disc ${name} (ดิสก์ไดรฟ์) ใน Zenless Zone Zero (ZZZ) โบนัสเซ็ต 2 ชิ้น/4 ชิ้น เหมาะกับตัวละครไหน`
      : `ข้อมูล Echo ${name} (เอคโค่) ใน Wuthering Waves (WuWa) สกิล เซ็ตโบนัส เหมาะกับตัวละครไหน`
    keywords = isZzz
      ? [`${name} zzz`, `${name} drive disc`, `zzz ดิสก์ไดรฟ์ ${name}`, `zzz ดิส ${name}`, `drive disc ${name}`]
      : [`${name} wuwa`, `echo ${name}`, `เอคโค่ ${name}`, `wuwa echo ${name}`, `${name} wuthering waves`]
  }
  description = description + (detail ? ` ${detail}` : '')

  const ogImage = item?.imageUrl ? resolveImageSrc(item.imageUrl) : SITE_SHARE_IMAGE
  const sectionLabel = opts.kind === 'weapon' ? game.weaponLabel : game.echoLabel

  return {
    title,
    description: truncate(description, 300),
    keywords,
    canonicalPath,
    ogType: 'article',
    ogImage,
    ogImageAlt: name ? `${name} — ${game.title}` : game.title,
    noindex: !name,
    jsonLd: [
      articleLd({
        headline: `${sectionLabel} ${name} | ${game.title} (${game.abbrev})`,
        description: truncate(description, 300),
        canonicalPath,
        image: item?.imageUrl ? resolveImageSrc(item.imageUrl) : undefined,
        datePublished: item?.createdAt,
        dateModified: item?.updatedAt,
        about: { '@type': 'VideoGame', name: game.title },
      }),
      breadcrumbLd([
        { name: 'หน้าแรก', path: '/' },
        { name: `ไกด์ ${game.title}`, path: `/game/guide/${opts.gameKey}` },
        { name: `${sectionLabel} ${name}` },
      ]),
    ],
  }
}
